import {
  Categories,
  useGetProductsByCategoryQuery,
  DiningOccasion
} from '@pizza-hut-us-development/client-core';
import {
  useEffect,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { OccasionApi } from '@/localization/constants';
import { localizationSelectors } from '@/localization/localizationSelectors';
import { selectOccasion } from '@/localization/selectors';
import { transformProductCategoryDataWithExcludingVariants } from './transformProductCategoryData';
import { RootState } from '@/rootStateTypes';
import { FinalizedCCTransforms } from './types';
import { LocalizedMenuPageSections } from '@/common/components/LocalizedProductTile/LocalizedProductTile.types';
import { debugModeSelectors } from '@/clientCore/redux/selectors/debugSelectors';
import { transformationDebugLogging } from '@/clientCore/debuggerLogging';
import { DebugModeOptions } from '@/clientCore/redux/debug/types';
import tel from '@/telemetry';
import { getErrMessage, getStatusCode } from '@/telemetry/helpers';
import { useGetProductsToHide } from '@/clientCore/menu/hooks/useGetProductsToHide';

declare type ProductCategoryResponse = {
  data: FinalizedCCTransforms | null | undefined;
  loading: boolean | null;
  error: unknown | null;
  storeID: string | null | undefined;
};

declare type Props = { categoryId: Categories; skip?: boolean };

declare type UseCCGetProducts = (arg0: Props) => ProductCategoryResponse;

export const isLocalizedMenuPageSection = (data: FinalizedCCTransforms | null | undefined): data is LocalizedMenuPageSections => {
  if (!data) return false;
  return (data as LocalizedMenuPageSections).productsForMainCategory !== undefined;
};

export const useCCGetProducts: UseCCGetProducts = ({ categoryId, skip }) => {
  const storeDetails = useSelector(localizationSelectors.storeDetails);
  const storeOccasion = useSelector(selectOccasion) as keyof typeof OccasionApi;
  const cmsEnvId = useSelector((state: RootState) => state.domain.cmsEnv.id);
  const occasion = OccasionApi[storeOccasion] as OccasionApi;
  const [storeNumber, setStoreNumber] = useState<string | undefined>(undefined);
  const [storeTimezone, setStoreTimeZone] = useState<string | undefined>(undefined);
  const debugEnabled = useSelector(debugModeSelectors.enabled);
  const debugMode = useSelector(debugModeSelectors.mode);

  const [{ enabled: improvedProductFilteringEnabled }] = useDecision('fr-web2468-improved-product-price-filtering');

  const shouldSkipQuery = skip || categoryId === Categories.DEALS || categoryId === Categories.PZONE || !storeNumber;

  useEffect(() => {
    if (storeDetails?.storeNumber) setStoreNumber(storeDetails.storeNumber);
    if (storeDetails?.storeTimezone) setStoreTimeZone(storeDetails.storeTimezone);
  }, [storeDetails]);

  const {
    currentData,
    isLoading,
    isFetching,
    error
  } = useGetProductsByCategoryQuery(
    {
      categoryId,
      environmentId: cmsEnvId,
      storeNumber,
      occasion: DiningOccasion[occasion as keyof typeof DiningOccasion],
      includeNutrition: true
    },
    {
      skip: shouldSkipQuery
    }
  );

  const hasFiredTelemetry = useRef(false);
  useEffect(() => {
    if (isFetching) {
      hasFiredTelemetry.current = false;
      return;
    }
    if (!!error && !hasFiredTelemetry.current) {
      hasFiredTelemetry.current = true;
      tel.addCustomEvent(tel.CUSTOM_EVENT_NAME.MENU.QUERY, 'failure', {
        category: categoryId,
        http_code: getStatusCode(error),
        err_msg: getErrMessage(error)
      });
    } else if (!hasFiredTelemetry.current) {
      hasFiredTelemetry.current = true;
      tel.addCustomEvent(tel.CUSTOM_EVENT_NAME.MENU.QUERY, 'success', {
        category: categoryId
      });
    }
  }, [isFetching, error, hasFiredTelemetry, categoryId]);

  const { data: hiddenProducts } = useGetProductsToHide();

  const isQueryLoading = isLoading || isFetching;

  if (shouldSkipQuery || isQueryLoading) {
    return {
      data: null,
      loading: !skip && isQueryLoading,
      error: null,
      storeID: null
    };
  }

  const data = currentData && storeTimezone
    ? transformProductCategoryDataWithExcludingVariants(
      categoryId,
      currentData,
      occasion,
      storeTimezone,
      improvedProductFilteringEnabled,
      hiddenProducts
    )
    : undefined;

  if (debugEnabled) {
    if (data) {
      transformationDebugLogging('useCCGetProducts', `useGetProductsByCategoryQuery Category: ${categoryId}`, currentData, data, debugMode, [
        DebugModeOptions.MENU,
        DebugModeOptions.TRANSFORMATIONS
      ]);
    }
  }

  return {
    data,
    loading: isQueryLoading,
    error,
    storeID: storeDetails?.storeNumber
  };
};
